import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createUserInputSchema, userRoleSchema, type CreateUserInput, type UserDto } from 'chery-contracts';
import { KeyRound, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Notice } from '../../components/ui/Notice';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { cn } from '../../lib/cn';
import { serverMessage } from '../../lib/form-helpers';
import { PILLAR_CLASSES, PILLAR_META, PILLARS } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { emptyMatrix, grantsFromMatrix, MATRIX_LEVELS, matrixFromGrants, roleHasPillarGrants, type AccessMatrix } from './user-display';

export interface TemporaryPasswordResult {
  email: string;
  password: string;
  mustChange: boolean;
}

export interface UserSheetProps {
  isOpen: boolean;
  /** Absent for a create. */
  user?: UserDto;
  onClose: () => void;
  onSaved: (user: UserDto, created: boolean) => void;
  onTemporaryPassword: (result: TemporaryPasswordResult) => void;
  onSignedOut: (user: UserDto, revoked: number) => void;
}

type UserForm = Omit<CreateUserInput, 'pillarAccess'>;

const EMPTY_VALUES: UserForm = { name: '', email: '', role: 'OWNER', status: 'ACTIVE' };

/**
 * Create and edit an account (plan §7). The pillar access matrix appears for an OWNER only — the other roles'
 * access follows from the role. On an existing account, the Security section resets the temporary password and
 * signs the user out everywhere; the service guards self-lockout and the last active Administrator.
 */
export const UserSheet = ({ isOpen, user, onClose, onSaved, onTemporaryPassword, onSignedOut }: UserSheetProps) => {
  const { t } = useI18nTranslation();
  const { user: me } = useAuth();
  const [matrix, setMatrix] = useState<AccessMatrix>(emptyMatrix);
  const [requireChange, setRequireChange] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = user !== undefined;

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserForm>({ resolver: zodResolver(createUserInputSchema.omit({ pillarAccess: true })), defaultValues: EMPTY_VALUES });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSubmitError(null);
    setRequireChange(true);
    setMatrix(user ? matrixFromGrants(user.pillarAccess) : emptyMatrix());
    reset(user ? { name: user.name, email: user.email, role: user.role, status: user.status } : EMPTY_VALUES);
  }, [isOpen, user, reset]);

  const createUser = useMutation(orpc.users.create.mutationOptions());
  const updateUser = useMutation(orpc.users.update.mutationOptions());
  const resetPassword = useMutation(orpc.users.resetPassword.mutationOptions());
  const revokeSessions = useMutation(orpc.users.revokeSessions.mutationOptions());

  const role = watch('role');
  const isSelf = isEdit && me?.id === user.id;
  const roleOptions: SelectOption[] = userRoleSchema.options.map((value) => ({ value, label: t(`roles.${value}`) }));

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    const pillarAccess = roleHasPillarGrants(values.role) ? grantsFromMatrix(matrix) : [];

    try {
      if (isEdit) {
        onSaved(await updateUser.mutateAsync({ id: user.id, ...values, pillarAccess }), false);
      } else {
        const { user: created, temporaryPassword } = await createUser.mutateAsync({ ...values, pillarAccess });

        onSaved(created, true);
        onTemporaryPassword({ email: created.email, password: temporaryPassword, mustChange: true });
      }
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  });

  const onReset = async (): Promise<void> => {
    if (!user) {
      return;
    }

    setSubmitError(null);

    try {
      const { user: updated, temporaryPassword } = await resetPassword.mutateAsync({ id: user.id, requireChange });

      onSaved(updated, false);
      onTemporaryPassword({ email: updated.email, password: temporaryPassword, mustChange: requireChange });
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  const onSignOutEverywhere = async (): Promise<void> => {
    if (!user) {
      return;
    }

    setSubmitError(null);

    try {
      const { revoked } = await revokeSessions.mutateAsync({ id: user.id });

      onSignedOut(user, revoked);
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('users.form.editTitle') : t('users.form.createTitle')}
      description={isEdit ? user.email : t('users.form.createSubtitle')}
      className="max-w-lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="user-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : isEdit ? t('actions.save') : t('actions.create')}
          </Button>
        </>
      }
    >
      <form
        id="user-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <FormSection title={t('users.columns.name')}>
          <FormField
            id="user-name"
            label={t('users.form.name')}
            error={errors.name?.message}
          >
            <Input
              id="user-name"
              autoComplete="off"
              hasError={Boolean(errors.name)}
              {...register('name')}
            />
          </FormField>
          <FormField
            id="user-email"
            label={t('users.form.email')}
            error={errors.email?.message}
          >
            <Input
              id="user-email"
              type="email"
              autoComplete="off"
              hasError={Boolean(errors.email)}
              {...register('email')}
            />
          </FormField>
          <FormField
            id="user-role"
            label={t('users.form.role')}
            error={errors.role?.message}
          >
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Select
                  id="user-role"
                  options={roleOptions}
                  disabled={isSelf}
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <Checkbox
                id="user-active"
                label={t('users.form.active')}
                checked={field.value !== 'INACTIVE'}
                disabled={isSelf}
                onChange={(event) => field.onChange(event.target.checked ? 'ACTIVE' : 'INACTIVE')}
              />
            )}
          />
        </FormSection>

        <FormSection title={t('users.form.access')}>
          {roleHasPillarGrants(role) ? (
            <>
              <p className="text-xs text-muted-foreground">{t('users.form.accessHint')}</p>
              <table
                className="w-full text-sm"
                data-testid="pillar-access-matrix"
              >
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className="py-1.5 text-start font-medium">{t('kpis.form.pillar')}</th>
                    {MATRIX_LEVELS.map((level) => (
                      <th
                        key={level}
                        className="w-16 py-1.5 text-center font-medium"
                      >
                        {t(`enums.access.${level}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PILLARS.map((pillar) => (
                    <tr
                      key={pillar}
                      className="border-t border-border"
                    >
                      <td className="py-2">
                        <span className="inline-flex items-center gap-2">
                          <span className={cn('size-2 rounded-full', PILLAR_CLASSES[pillar].dot)} />
                          {PILLAR_META[pillar].label}
                        </span>
                      </td>
                      {MATRIX_LEVELS.map((level) => (
                        <td
                          key={level}
                          className="py-2 text-center"
                        >
                          <input
                            type="radio"
                            name={`access-${pillar}`}
                            aria-label={`${PILLAR_META[pillar].label}: ${t(`enums.access.${level}`)}`}
                            checked={matrix[pillar] === level}
                            onChange={() => setMatrix((current) => ({ ...current, [pillar]: level }))}
                            className="size-4 cursor-pointer accent-primary"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('users.form.accessByRole')}</p>
          )}
        </FormSection>

        {submitError ? <Notice tone="danger">{submitError}</Notice> : null}
      </form>

      {isEdit ? (
        <div className="mt-6 space-y-4 border-t border-border pt-6">
          <FormSection title={t('users.form.security')}>
            <div className="space-y-2 rounded-md border border-border p-4">
              <p className="text-sm font-medium text-card-foreground">{t('users.form.resetTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('users.form.resetBody')}</p>
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <Checkbox
                  id="user-require-change"
                  label={t('users.form.requireChange')}
                  checked={requireChange}
                  onChange={(event) => setRequireChange(event.target.checked)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  isLoading={resetPassword.isPending}
                  onClick={() => void onReset()}
                >
                  <KeyRound className="size-3.5" />
                  {t('users.form.reset')}
                </Button>
              </div>
            </div>

            <div className="space-y-2 rounded-md border border-border p-4">
              <p className="text-sm font-medium text-card-foreground">{t('users.form.signOutTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('users.form.signOutBody')}</p>
              <div className="flex justify-end pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  isLoading={revokeSessions.isPending}
                  onClick={() => void onSignOutEverywhere()}
                >
                  <LogOut className="size-3.5" />
                  {t('users.form.signOut')}
                </Button>
              </div>
            </div>
          </FormSection>
        </div>
      ) : null}
    </Sheet>
  );
};
